import { SceneKey } from '@models/scene-keys';
import { GameConfigService } from '@services/core/GameConfigService';
import { BoardService } from '@services/game/BoardService';
import { LayoutService } from '@services/ui/LayoutService';
import Phaser from 'phaser';
import { createBoard } from '../ui/Board';
import { GameBackground } from './GameBackground';

export default class AdminLayout extends Phaser.Scene {
  private layout!: LayoutService;
  private configService!: GameConfigService;
  private board!: ReturnType<typeof createBoard>;
  private gameBg!: GameBackground;

  constructor() {
    super(SceneKey.AdminLayout);
  }

  async create() {
    // Initialize core services
    const boardService = new BoardService();
    this.configService = new GameConfigService();
    this.layout = new LayoutService(this.scale);
    this.gameBg = new GameBackground(this, this.layout);
    this.gameBg.create();

    // Create a board view
    const boardConfig = this.configService.boardConfig!;
    this.board = createBoard(this, boardConfig, boardService, {
      startPosition: this.configService.gameStartPosition.tile,
    });
    // create decorations around board
    this.gameBg.setDecorations(this.board.layout.worldHeight);
    this.launchUIScene();
    this.setupDevDragScroll();
  }

  private launchUIScene(): void {
    // Launch the UI Scene in parallel
    this.scene.launch(SceneKey.UI);

    // Ensure UI is on top (optional, usually launch order handles this)
    this.scene.bringToTop(SceneKey.UI);
  }

  private setupDevDragScroll(): void {
    let lastY = 0;
    let dragging = false;

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragging = true;
      lastY = p.y;
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging || !p.isDown) return;
      // Keep lastY in sync even when a decor image is being dragged so the
      // camera doesn't jump when the decor drag ends.
      if (this.data.get('decorDragging')) {
        lastY = p.y;
        return;
      }
      const dy = lastY - p.y;
      lastY = p.y;
      this.cameras.main.scrollY += dy;
    });

    this.input.on('pointerup', () => {
      dragging = false;
    });

    this.input.on(
      'wheel',
      (
        _pointer: Phaser.Input.Pointer,
        _currentlyOver: Phaser.GameObjects.GameObject[],
        _deltaX: number,
        deltaY: number,
      ) => {
        this.cameras.main.scrollY += deltaY;
      },
    );
  }

  shutdown() {
    // Clean up background resize listener
    if (this.gameBg) {
      this.gameBg.destroy();
    }

    // Clean up board resources (removes resize listener)
    if (this.board) {
      this.board.destroy();
    }
  }
}
